import { LandingBlock, LandingBlockType, LandingPage } from '../types'
import { slugify } from '../utils/format'
import { blockDefinition } from '../data/blockLibrary'
import { DEFAULT_THANK_YOU } from '../data/thankYou'
import {
  AVA_TEMPLATE_VERSION,
  LandingTemplateId,
  templateBlocks,
  templateKeyFor,
} from '../data/landingTemplates'
import { supabase } from './supabaseClient'
import {
  blockRow,
  landingConfig,
  landingContentRow,
  landingRow,
  pageConfigFromBlocks,
  toBlock,
  toLanding,
} from './mappers'
import { muteRealtime } from '../utils/realtimeMute'
import {
  cacheLanding,
  getDb,
  refresh,
  requireOrganizationId,
  saveConfig,
} from './store'
import { DEFAULT_FLOATING } from '../data/floating'
import { DEFAULT_BACKGROUND } from '../data/background'
import { DEFAULT_LANDING_META_PIXEL, DEFAULT_META_PIXEL_EVENTS } from '../data/metaPixel'
import { isValidPixelId } from '../utils/metaPixel'
import { newId } from './authService'
import { GOOGLE_CALENDAR, META_PIXEL, getIntegration } from './integrationService'
import { logFailure, logSuccess } from '../utils/diagnostics'
import { timed } from '../utils/perf'

export function listLandings(organizationId: string): LandingPage[] {
  return getDb().landings.filter((l) => l.organizationId === organizationId)
}

export function getLanding(landingId: string): LandingPage | undefined {
  return getDb().landings.find((l) => l.id === landingId)
}

export function getLandingBySlug(slug: string): LandingPage | undefined {
  return getDb().landings.find((l) => l.slug === slug)
}

export function newBlock(type: LandingBlockType): LandingBlock {
  const definition = blockDefinition(type)
  return {
    id: newId('block'),
    type,
    visible: true,
    spacing: 'normal',
    config: JSON.parse(JSON.stringify(definition.defaultConfig)),
  }
}

/** Descarga una landing publicada (visitante anónimo). */
export async function fetchPublicLanding(
  slug: string,
): Promise<LandingPage | null> {
  const { data, error } = await supabase
    .from('landing_pages')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .is('deleted_at', null)
    .maybeSingle()

  if (error) {
    // Un error de red o de RLS NO significa que la landing no exista: antes se
    // devolvía null y la página mostraba un falso «esta página no existe».
    // Ahora se propaga para que la vista reintente y muestre el error real.
    logFailure('fetchPublicLanding', error, { slug })
    throw new Error(error.message)
  }
  if (!data) {
    /*
     * Diagnóstico: distingue «el slug no existe» de «existe pero no está
     * publicado». Esta segunda situación era la causa real del falso 404:
     * guardar la landing reescribía su estado a borrador.
     */
    const { data: any404 } = await supabase
      .from('landing_pages')
      .select('id, slug, status, deleted_at')
      .eq('slug', slug)
      .maybeSingle()
    logFailure(
      'fetchPublicLanding',
      {
        message: any404
          ? `La landing "${slug}" existe pero no está publicada (status="${any404.status}"${any404.deleted_at ? ', en la papelera' : ''}). Pulsa «Publicar» en el editor.`
          : `No hay ninguna landing con el slug "${slug}" visible para un visitante anónimo. Comprueba el slug y que la lectura pública esté permitida.`,
      },
      { slug, encontrada: Boolean(any404), estado: any404?.status },
    )
    return null
  }

  const [{ data: blocks }, { data: settings, error: settingsError }] =
    await Promise.all([
      supabase
        .from('landing_blocks')
        .select('*')
        .eq('landing_id', data.id)
        .order('position'),
      supabase
        .from('organization_settings')
        .select('value')
        .eq('organization_id', data.organization_id)
        .eq('key', `landing_config:${data.id}`)
        .maybeSingle(),
    ])
  if (settingsError) {
    logFailure('fetchPublicLanding.config', settingsError, { slug })
  }

  /*
   * `organization_settings` está protegida por RLS para los miembros de la
   * organización, así que un visitante anónimo recibe null aunque la fila
   * exista. Esa era la causa de que la landing pública perdiera el logotipo,
   * los elementos flotantes y la configuración de la página de gracias
   * mientras el constructor (con sesión) los mostraba bien. La copia guardada
   * en `landing_blocks.style.page` sí es legible públicamente.
   */
  const embedded = pageConfigFromBlocks(blocks ?? [])
  const config = settings?.value ?? embedded ?? {}
  if (!settings?.value) {
    logFailure(
      'fetchPublicLanding.config',
      {
        message: embedded
          ? 'organization_settings no es legible sin sesión: se usa la copia pública guardada en los bloques.'
          : 'No hay configuración de página legible: se aplican los valores por defecto. Guarda y publica la landing de nuevo para generar la copia pública.',
      },
      { slug },
    )
  }

  logSuccess('fetchPublicLanding', {
    slug,
    landingId: data.id,
    organizationId: data.organization_id,
    formId: data.form_id,
    bloques: (blocks ?? []).length,
    configuracion: settings?.value ? 'ajustes' : embedded ? 'bloques' : 'defecto',
    logo: Boolean((config as { logoUrl?: string }).logoUrl),
    flotantes: Boolean((config as { floating?: unknown }).floating),
  })

  return toLanding(data, config, (blocks ?? []).map(toBlock))
}

export async function createLanding(
  organizationId: string,
  name: string,
  formId?: string,
  template: LandingTemplateId = 'caso_exito',
): Promise<LandingPage> {
  // Sin organización resuelta, el insert sería rechazado por RLS con un
  // mensaje incomprensible. Se corta antes, con una explicación clara.
  requireOrganizationId(organizationId, 'crear la landing')
  // El calendario global de la empresa se aplica desde el primer momento.
  const calendar = await getIntegration(organizationId, GOOGLE_CALENDAR)
  const globalUrl = String(calendar?.config?.bookingUrl ?? '')
  // El Pixel global de la organización, si existe, se hereda desde el primer
  // momento (igual que el calendario). Un Pixel ID mal formado nunca llega a
  // guardarse: se descarta y la landing nace sin Meta Pixel.
  const metaPixelIntegration = await getIntegration(organizationId, META_PIXEL)
  const metaPixelConfig = (metaPixelIntegration?.config ?? {}) as {
    pixelId?: string
    events?: typeof DEFAULT_META_PIXEL_EVENTS
  }
  const metaPixelId = isValidPixelId(metaPixelConfig.pixelId) ? metaPixelConfig.pixelId : undefined
  const blocks = templateBlocks(template, () => newId()).map((block) =>
    block.type === 'calendario'
      ? { ...block, config: { ...block.config, globalUrl } }
      : block,
  )

  const landing: LandingPage = {
    id: newId('landing'),
    organizationId,
    formId,
    name,
    slug: slugify(name) || `landing-${Date.now().toString(36)}`,
    status: 'borrador',
    views: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    pageConfig: {
      theme: 'blanco_premium',
      primaryColor: '#3D8BFF',
      fontFamily: 'Inter',
      // Identificación estable de la plantilla oficial ya aplicada.
      templateKey: templateKeyFor(template),
      templateVersion: AVA_TEMPLATE_VERSION,
      // Los bloques provienen de la plantilla elegida, con ids UUID válidos.
      blocks,
      // Página de gracias con el paso final de WhatsApp ya montado.
      thankYou: { ...DEFAULT_THANK_YOU },
      // WhatsApp flotante apagado; avisos de actividad encendidos.
      floating: JSON.parse(JSON.stringify(DEFAULT_FLOATING)),
      // Sin efecto de fondo: la landing nace exactamente como hasta ahora.
      background: { ...DEFAULT_BACKGROUND },
      // Usa el Pixel global de la organización si está conectado; si no, nace
      // desactivado (cero peticiones a Meta) hasta que se configure alguno.
      metaPixel: metaPixelIntegration
        ? {
            enabled: Boolean(metaPixelIntegration.isActive) && Boolean(metaPixelId),
            pixelId: metaPixelId,
            events: { ...DEFAULT_META_PIXEL_EVENTS, ...(metaPixelConfig.events ?? {}) },
            override: false,
          }
        : { ...DEFAULT_LANDING_META_PIXEL },
    },
  }
  const { error } = await supabase.from('landing_pages').insert(landingRow(landing))
  if (error) throw new Error(error.message)
  await saveLanding(landing)
  return landing
}

/**
 * Guarda la landing completa: cabecera, configuración y bloques.
 *
 * Rendimiento: las cuatro operaciones independientes se lanzan EN PARALELO en
 * lugar de una tras otra (antes eran cinco viajes secuenciales), y al terminar
 * se parchea la caché local en vez de llamar a `refresh()`, que recargaba todo
 * el espacio de trabajo (~17 consultas: leads, notas, actividades, envíos,
 * respuestas, equipo, biblioteca…) en cada clic de Guardar. Nada de eso cambia
 * al guardar una landing.
 */
export async function saveLanding(landing: LandingPage): Promise<void> {
  // La configuración de página viaja también dentro de cada bloque para que
  // el visitante anónimo la reciba (organization_settings no es pública).
  // Las escrituras propias no deben provocar una recarga completa del espacio
  // de trabajo a través de Realtime (ver utils/realtimeMute.ts).
  muteRealtime()
  const total = performance.now()
  const serializeStart = performance.now()
  const publicConfig = landingConfig(landing)
  /*
   * La copia pública de la configuración se guarda SOLO en el primer bloque.
   * Antes viajaba dentro de cada uno de los bloques, así que una landing de
   * diez bloques enviaba diez veces el mismo objeto (tema, colores, logo,
   * elementos flotantes, página de gracias…) en cada guardado.
   * `pageConfigFromBlocks` lee el primer bloque que la contenga, así que el
   * visitante anónimo la sigue recibiendo igual.
   */
  const rows = landing.pageConfig.blocks.map((block, index) =>
    blockRow(
      block,
      landing.id,
      landing.organizationId,
      index,
      index === 0 ? publicConfig : undefined,
    ),
  )
  const payloadKb = Math.round(JSON.stringify(rows).length / 1024)
  const serializeMs = performance.now() - serializeStart
  console.log(
    `[AVA SAVE] serializar: ${serializeMs.toFixed(0)} ms · ${rows.length} bloques · ${payloadKb} KB`,
  )

  const [headResult, configResult, existingResult, blocksResult] =
    await Promise.all([
      // Se usa upsert para que también funcione si la fila no existía. NO se
      // escribe `status`: publicar y despublicar es cosa de setLandingStatus.
      timed('[AVA SAVE] landing (landing_pages)', async () =>
        supabase
          .from('landing_pages')
          .upsert(landingContentRow(landing), { onConflict: 'id' }),
      ),
      timed('[AVA SAVE] config (organization_settings)', async () =>
        saveConfig(landing.organizationId, 'landing', landing.id, publicConfig)
          .then(() => ({ error: null }))
          .catch((err: unknown) => ({
            error: {
              message: err instanceof Error ? err.message : 'error desconocido',
            },
          })),
      ),
      timed('[AVA SAVE] leer bloques', async () =>
        supabase
          .from('landing_blocks')
          .select('id')
          .eq('landing_id', landing.id),
      ),
      timed('[AVA SAVE] escribir bloques', async () =>
        rows.length
          ? supabase
              .from('landing_blocks')
              .upsert(rows, { onConflict: 'id' })
          : { error: null },
      ),
    ])

  if (headResult.error) {
    throw new Error(
      `No se ha podido guardar la landing: ${headResult.error.message}`,
    )
  }
  if (configResult.error) {
    throw new Error(
      `No se ha podido guardar la configuración de la landing: ${configResult.error.message}`,
    )
  }
  if (blocksResult.error) {
    throw new Error(
      `No se han podido guardar los bloques: ${blocksResult.error.message}`,
    )
  }
  if (existingResult.error) {
    throw new Error(
      `No se han podido leer los bloques: ${existingResult.error.message}`,
    )
  }

  // El borrado depende de la lectura anterior, así que es el único paso que
  // queda en un segundo viaje, y solo cuando de verdad hay algo que borrar.
  const keep = landing.pageConfig.blocks.map((b) => b.id)
  const remove = (existingResult.data ?? [])
    .map((row: { id: string }) => row.id)
    .filter((id: string) => !keep.includes(id))
  if (remove.length) {
    const { error: deleteError } = await timed(
      '[AVA SAVE] borrar bloques',
      async () => supabase.from('landing_blocks').delete().in('id', remove),
    )
    if (deleteError) {
      throw new Error(
        `No se han podido eliminar los bloques borrados: ${deleteError.message}`,
      )
    }
  }

  const cacheStart = performance.now()
  cacheLanding(landing)
  console.log(
    `[AVA SAVE] cache: ${(performance.now() - cacheStart).toFixed(0)} ms`,
  )
  // Se amplía el silencio: los eventos de Realtime de estas escrituras pueden
  // llegar con retraso, y ninguno aporta nada que no esté ya en la caché.
  muteRealtime()
  console.log(
    `[AVA SAVE] TOTAL: ${(performance.now() - total).toFixed(0)} ms`,
  )
}

export async function setLandingStatus(
  landingId: string,
  status: LandingPage['status'],
): Promise<void> {
  // Tras `saveLanding` la caché ya contiene el borrador recién guardado, así
  // que la copia pública que se escribe aquí es la actual y no una anterior.
  const landing = getLanding(landingId)
  if (!landing) return
  muteRealtime()
  const total = performance.now()
  const next: LandingPage = { ...landing, status }
  const [headResult, configResult] = await Promise.all([
    timed('[AVA PUBLISH] estado (landing_pages)', async () =>
      supabase
        .from('landing_pages')
        .update({
          status: status === 'publicada' ? 'published' : 'draft',
          published_at:
            status === 'publicada' ? new Date().toISOString() : null,
        })
        .eq('id', landingId),
    ),
    timed('[AVA PUBLISH] copia pública (organization_settings)', async () =>
      saveConfig(landing.organizationId, 'landing', landingId, {
        ...landingConfig(next),
        status,
      })
        .then(() => ({ error: null }))
        .catch((err: unknown) => ({
          error: {
            message: err instanceof Error ? err.message : 'error desconocido',
          },
        })),
    ),
  ])
  if (headResult.error) throw new Error(headResult.error.message)
  if (configResult.error) {
    throw new Error(
      `No se ha podido actualizar la publicación: ${configResult.error.message}`,
    )
  }
  cacheLanding(next)
  muteRealtime()
  console.log(
    `[AVA PUBLISH] TOTAL: ${(performance.now() - total).toFixed(0)} ms`,
  )
}

export async function duplicateLanding(
  landingId: string,
): Promise<LandingPage | undefined> {
  const original = getLanding(landingId)
  if (!original) return undefined
  const copy: LandingPage = {
    ...(JSON.parse(JSON.stringify(original)) as LandingPage),
    id: newId('landing'),
    name: `${original.name} (copia)`,
    slug: `${original.slug}-copia-${Date.now().toString(36)}`,
    status: 'borrador',
    views: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  copy.pageConfig.blocks = copy.pageConfig.blocks.map((block) => ({
    ...block,
    id: newId('block'),
  }))
  const { error } = await supabase.from('landing_pages').insert(landingRow(copy))
  if (error) throw new Error(error.message)
  await saveLanding(copy)
  return copy
}

/** Borrado lógico: la landing pasa a la papelera de landings. */
export async function deleteLanding(landingId: string): Promise<void> {
  const { error } = await supabase
    .from('landing_pages')
    .update({ deleted_at: new Date().toISOString(), status: 'draft' })
    .eq('id', landingId)
  if (error) throw new Error(error.message)
  await refresh()
}

export async function restoreLanding(landingId: string): Promise<void> {
  const { error } = await supabase
    .from('landing_pages')
    .update({ deleted_at: null })
    .eq('id', landingId)
  if (error) throw new Error(error.message)
  await refresh()
}
